/**
 * Yelp Restaurant Search Component
 * Enhanced restaurant search using Yelp Fusion API with fallback to local data
 */

import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Star, Clock, DollarSign, Phone, Globe, ChevronDown, Filter, ExternalLink } from 'lucide-react';
import yelpAPI from '../utils/yelpAPI';
import { logger } from '../utils/logger';

const YelpRestaurantSearch = ({
  onRestaurantSelect,
  selectedLocation = '',
  className = '',
  placeholder = 'Search for restaurants...'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // Track if user has performed a search
  const [filters, setFilters] = useState({
    priceLevel: '',
    radius: 5000,
    cuisineType: '',
    sortBy: 'best_match',
    openNow: false
  });

  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && !searchRef.current.contains(event.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // No auto-population - user must enter both fields

  // Autocomplete suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const results = await yelpAPI.autocomplete(searchQuery);
        setSuggestions(results.map((term, index) => ({
          id: `suggestion-${index}`,
          name: term.text,
          description: term.text
        })));
      } catch (error) {
        logger.error('Autocomplete error:', error);
        setSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, selectedLocation]);

  // Search restaurants
  const handleSearch = async () => {
    // Require location to be entered
    if (!locationQuery.trim()) {
      logger.debug('Location is required to search');
      return;
    }

    setLoading(true);
    setHasSearched(true); // Mark that user has performed a search
    try {
      const searchOptions = {
        term: searchQuery.trim() || 'restaurants',
        location: locationQuery.trim(),
        radius: filters.radius,
        limit: 20,
        sort_by: filters.sortBy,
        open_now: filters.openNow
      };

      // Add price filter if selected
      if (filters.priceLevel) {
        searchOptions.price = filters.priceLevel.length; // Yelp uses 1-4 for price levels
      }

      // Add category filter if cuisine type is selected
      if (filters.cuisineType) {
        const cuisineMap = {
          'Italian': 'italian',
          'Chinese': 'chinese',
          'Mexican': 'mexican',
          'American': 'tradamerican',
          'Japanese': 'japanese',
          'Thai': 'thai',
          'Indian': 'indpak',
          'French': 'french',
          'Pizza': 'pizza',
          'Sushi': 'sushi'
        };
        searchOptions.categories = cuisineMap[filters.cuisineType] || filters.cuisineType.toLowerCase();
      }

      const results = await yelpAPI.searchBusinesses(searchOptions);
      setRestaurants(results);
      setShowSuggestions(false);
    } catch (error) {
      logger.error('Restaurant search error:', error);
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = async (suggestion) => {
    setSearchQuery(suggestion.name);
    setShowSuggestions(false);
    if (locationQuery.trim()) {
      handleSearch();
    }
  };

  // Handle restaurant selection
  const handleRestaurantSelect = (restaurant) => {
    setSelectedRestaurant(restaurant);
    if (onRestaurantSelect) {
      onRestaurantSelect(restaurant);
    }
  };

  // Filter options
  const priceOptions = ['$', '$$', '$$$', '$$$$'];
  const cuisineOptions = ['Italian', 'Chinese', 'Mexican', 'American', 'Japanese', 'Thai', 'Indian', 'French', 'Pizza', 'Sushi'];
  const sortOptions = [
    { value: 'best_match', label: 'Best Match' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'review_count', label: 'Most Reviewed' },
    { value: 'distance', label: 'Distance' }
  ];

  return (
    <div className={`yelp-restaurant-search ${className}`}>
      {/* Search Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Restaurant Selection
          </h3>
          {yelpAPI.isConfigured() && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
              <img src="/yelp_logos/yelp_burst.png" alt="Yelp" className="w-3 h-3 mr-1" />
              Yelp Search
            </span>
          )}
        </div>
      </div>

      {/* Two Search Fields Side by Side */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Restaurant Name/Type Search Field - NOW FIRST */}
        <div className="relative" ref={searchRef}>
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => {
              // Delay to allow suggestion click to register
              setTimeout(() => setShowSuggestions(false), 150);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
            placeholder={placeholder}
            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            autoFocus
          />

          {/* Autocomplete Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            >
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                >
                  <p className="text-sm font-medium text-gray-900">{suggestion.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Location Search Field - NOW SECOND */}
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={locationQuery}
            onChange={(e) => setLocationQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
            placeholder="City, State, or ZIP code"
            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Search Button and Filters Toggle */}
      <div className="mb-4 flex gap-2">
        <button
          onClick={handleSearch}
          disabled={!locationQuery.trim()}
          className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
            locationQuery.trim()
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Search Restaurants
        </button>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Filter className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Price Level Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price Range
              </label>
              <select
                value={filters.priceLevel}
                onChange={(e) => setFilters(prev => ({ ...prev, priceLevel: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="">Any Price</option>
                {priceOptions.map(price => (
                  <option key={price} value={price}>{price}</option>
                ))}
              </select>
            </div>

            {/* Cuisine Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cuisine Type
              </label>
              <select
                value={filters.cuisineType}
                onChange={(e) => setFilters(prev => ({ ...prev, cuisineType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="">Any Cuisine</option>
                {cuisineOptions.map(cuisine => (
                  <option key={cuisine} value={cuisine}>{cuisine}</option>
                ))}
              </select>
            </div>

            {/* Sort By Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Radius Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Radius
              </label>
              <select
                value={filters.radius}
                onChange={(e) => setFilters(prev => ({ ...prev, radius: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value={1000}>1 km</option>
                <option value={2000}>2 km</option>
                <option value={5000}>5 km</option>
                <option value={10000}>10 km</option>
                <option value={25000}>25 km</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.openNow}
                onChange={(e) => setFilters(prev => ({ ...prev, openNow: e.target.checked }))}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="ml-2 text-sm text-gray-700">Open now</span>
            </label>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => handleSearch()}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:ring-2 focus:ring-red-500"
            >
              Apply Filters
            </button>
            <button
              onClick={() => {
                setFilters({ priceLevel: '', radius: 5000, cuisineType: '', sortBy: 'best_match', openNow: false });
                setShowFilters(false);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <span className="ml-2 text-gray-600">Searching restaurants...</span>
        </div>
      )}

      {/* Restaurant Results */}
      {!loading && restaurants.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">
            Found {restaurants.length} restaurant{restaurants.length !== 1 ? 's' : ''}
          </h4>

          {restaurants.map((restaurant) => (
            <RestaurantCard
              key={restaurant.id}
              restaurant={restaurant}
              isSelected={selectedRestaurant?.id === restaurant.id}
              onSelect={() => handleRestaurantSelect(restaurant)}
            />
          ))}
        </div>
      )}

      {/* No Results or API Not Configured */}
      {!loading && restaurants.length === 0 && (
        <div className="text-center py-8">
          {!yelpAPI.isConfigured() ? (
            <div className="space-y-4">
              <div className="text-gray-500 mb-2">
                Yelp API not configured
              </div>
              <div className="text-sm text-gray-400 mb-4">
                To use restaurant search, configure your Yelp API key in environment settings
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Manual Restaurant Entry</h4>
                <p className="text-sm text-blue-700 mb-3">
                  You can manually enter restaurant information for your meeting
                </p>
                <button
                  onClick={() => {
                    // Create a manual restaurant entry
                    const manualRestaurant = {
                      id: 'manual-' + Date.now(),
                      name: 'Restaurant (Manual Entry)',
                      address: 'To be determined',
                      phone: 'To be determined',
                      cuisineType: 'Various',
                      priceRange: '$$',
                      rating: 0,
                      deliveryAvailable: false,
                      estimatedDeliveryTime: 'N/A',
                      isYelpBusiness: false,
                      isManualEntry: true
                    };
                    handleRestaurantSelect(manualRestaurant);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                >
                  Continue with Manual Entry
                </button>
              </div>
            </div>
          ) : hasSearched ? (
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="font-medium text-orange-800 mb-2">No Restaurants Found</h4>
                <p className="text-sm text-orange-700 mb-3">
                  Yelp API didn't return any restaurants for "{searchQuery}" near {locationQuery || 'this location'}.
                </p>
                <div className="space-y-2 text-sm text-orange-600">
                  <p>This could be due to:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>CORS restrictions blocking the API call</li>
                    <li>Invalid Yelp API key or rate limiting</li>
                    <li>No restaurants matching your search criteria</li>
                    <li>Network connectivity issues</li>
                  </ul>
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setRestaurants([]);
                      setHasSearched(false);
                    }}
                    className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
                  >
                    Clear Search
                  </button>
                  <button
                    onClick={() => handleSearch()}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    Retry
                  </button>
                </div>
              </div>

              {/* Manual entry option */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Manual Restaurant Entry</h4>
                <p className="text-sm text-blue-700 mb-3">
                  As an alternative, you can manually enter restaurant information
                </p>
                <button
                  onClick={() => {
                    const manualRestaurant = {
                      id: 'manual-' + Date.now(),
                      name: 'Manual Restaurant Entry',
                      address: locationQuery || 'Address to be determined',
                      phone: 'Phone to be determined',
                      cuisineType: 'Various',
                      priceRange: '$$',
                      rating: 0,
                      deliveryAvailable: false,
                      estimatedDeliveryTime: 'N/A',
                      isYelpBusiness: false,
                      isManualEntry: true
                    };
                    handleRestaurantSelect(manualRestaurant);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                >
                  Continue with Manual Entry
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="text-gray-600 mb-2">Search for restaurants using Yelp</p>
              <p className="text-sm text-gray-500">Enter a restaurant name, cuisine type, or food item above</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Restaurant Card Component
const RestaurantCard = ({ restaurant, isSelected, onSelect }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div
      className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
        isSelected
          ? 'border-red-500 bg-red-50 shadow-md'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h5 className="font-semibold text-gray-900">{restaurant.name}</h5>
            {restaurant.isYelpBusiness && (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                <img src="/yelp_logos/yelp_burst.png" alt="Yelp" className="w-3 h-3 mr-1" />
                Yelp
              </span>
            )}
            {restaurant.distance && (
              <span className="text-xs text-gray-500">
                {(restaurant.distance / 1000).toFixed(1)}km away
              </span>
            )}
          </div>

          <div className="space-y-1 text-sm text-gray-600">
            <div className="flex items-center gap-4">
              <span className="flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                {restaurant.address}
              </span>
            </div>

            <div className="flex items-center gap-4">
              {restaurant.rating > 0 && (
                <span className="flex items-center">
                  <Star className="w-4 h-4 mr-1 text-yellow-400 fill-current" />
                  {restaurant.rating}
                  {restaurant.reviewCount && (
                    <span className="ml-1 text-gray-500">({restaurant.reviewCount})</span>
                  )}
                </span>
              )}

              <span className="flex items-center">
                <DollarSign className="w-4 h-4 mr-1" />
                {restaurant.priceRange || '$$'}
              </span>

              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {restaurant.estimatedDeliveryTime || '30-45 min'}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                {restaurant.cuisineType}
              </span>
              {restaurant.deliveryAvailable && (
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                  Delivery Available
                </span>
              )}
              {restaurant.categories && restaurant.categories.length > 1 && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                  +{restaurant.categories.length - 1} more
                </span>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
            {restaurant.phone && restaurant.phone !== 'Contact for details' && (
              <span className="flex items-center">
                <Phone className="w-4 h-4 mr-1" />
                {restaurant.phone}
              </span>
            )}
            {restaurant.url && (
              <a
                href={restaurant.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center text-red-600 hover:text-red-700"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Yelp Page
              </a>
            )}
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowDetails(!showDetails);
          }}
          className="ml-4 p-2 hover:bg-gray-100 rounded"
        >
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Expanded Details */}
      {showDetails && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          {restaurant.menu && restaurant.menu.length > 0 && (
            <div>
              <h6 className="font-medium text-gray-900 mb-2">Sample Menu</h6>
              <div className="space-y-2">
                {restaurant.menu[0]?.items?.slice(0, 3).map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-700">{item.name}</span>
                    <span className="text-gray-900 font-medium">${item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {restaurant.categories && restaurant.categories.length > 0 && (
            <div className="mt-4">
              <h6 className="font-medium text-gray-900 mb-2">Categories</h6>
              <div className="flex flex-wrap gap-1">
                {restaurant.categories.map((category, index) => (
                  <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                    {category.title}
                  </span>
                ))}
              </div>
            </div>
          )}

          {restaurant.imageUrl && (
            <div className="mt-4">
              <img
                src={restaurant.imageUrl}
                alt={restaurant.name}
                className="w-full h-32 object-cover rounded-md"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default YelpRestaurantSearch;